import { useCallback } from 'react';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { URLParameters } from '@/app/common/urlParameters';
import { createHotelRoomClickedPayload } from '@/app/common/starTrack/events/hotelRoomClicked.ts';
import { createHotelRoomOptionClickedPayload } from '@/app/common/starTrack/events/hotelRoomOptionClicked.ts';
import { createPopupCancellationPolicyShownPayload } from '@/app/common/starTrack/events/popupCancellationPolicyShown.ts';
import { createPopupCancellationPolicyClickedPayload } from '@/app/common/starTrack/events/popupCancellationPolicyClicked.ts';

/**
 * Fires HOTEL_ROOM_CLICKED + HOTEL_ROOM_OPTION_CLICKED together for the DP-evolution layout.
 *
 * Both events are sent when the user commits a room/option by clicking "Continue", never on mere
 * selection: straight to flights when the room has a single cancellation option (trip overview /
 * mobile room card), from the cancellation-type modal when there are several, and from the room
 * information modal. Keeping the pair in one place guarantees they always fire together with the
 * same room/meal plan.
 */
export function useTrackEvoRoomSelection() {
  const { starTrack } = useCommonClient();
  const initialParams = useInitialParams();
  const { productDetailKey } = initialParams;
  const searchParams = getSearchParams();
  const searchId = searchParams[URLParameters.SEARCH_ID];

  const trackRoomSelection = useCallback(
    (room: RoomType, mealPlan: MealPlanType) => {
      starTrack.hotelRoomClick(
        createHotelRoomClickedPayload({
          searchId: searchId || '',
          initialParams,
          room,
          providerId: mealPlan.supplier?.id?.toString(),
        })
      );
      starTrack.hotelRoomOptionClicked(
        createHotelRoomOptionClickedPayload({
          searchId: searchId || '',
          initialParams,
          room,
          mealPlan,
        })
      );
    },
    [starTrack, searchId, initialParams.internalHotelId, initialParams.productDetailKey]
  );

  const trackCancellationPopupShown = useCallback(
    (roomId: string) => {
      starTrack.popupCancellationPolicyShown(
        createPopupCancellationPolicyShownPayload({
          searchId: searchId || '',
          roomId,
          productDetailKey,
        })
      );
    },
    [starTrack, searchId, productDetailKey]
  );

  const trackCancellationPopupClicked = useCallback(
    (roomId: string, optionIndex: number, desiredProfitPricingId: string) => {
      starTrack.popupCancellationPolicyClicked(
        createPopupCancellationPolicyClickedPayload({
          searchId: searchId || '',
          roomId,
          productDetailKey,
          optionIndex,
          desiredProfitPricingId,
        })
      );
    },
    [starTrack, searchId, productDetailKey]
  );

  return { trackRoomSelection, trackCancellationPopupShown, trackCancellationPopupClicked };
}
